import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Button, IconButton } from "./Button";
import { Icon } from "./Icon";
import { Skeleton } from "./Skeleton";
import { useI18n } from "../i18n";
import { useTheme } from "../lib/theme";
import { useAuth } from "../lib/AuthContext";
import { useMe } from "../lib/queries";

function LangButton() {
  const { lang, setLang, t } = useI18n();
  return (
    <button type="button" className="btn btn-ghost btn-icon" style={{ fontWeight: 700, fontSize: 14 }} aria-label={`${t("lang.toggle")} ${t("lang.aria")}`} onClick={() => setLang(lang === "th" ? "en" : "th")}>
      {t("lang.toggle")}
    </button>
  );
}

function ThemeButton() {
  const { t } = useI18n();
  const { isDark, toggle } = useTheme();
  return <IconButton variant="ghost" icon={isDark ? "sun" : "moon"} aria-label={t("theme.toggle")} onClick={toggle} />;
}

function AccountSlot() {
  const { t } = useI18n();
  const { status } = useAuth();
  const me = useMe();
  const { pathname } = useLocation();
  if (status === "loading") return <Skeleton shape="custom" style={{ width: 44, height: 32, borderRadius: 16 }} />;
  if (status === "anonymous") {
    return (
      <Button size="sm" to={`/login?returnTo=${encodeURIComponent(pathname)}`}>
        {t("home.login")}
      </Button>
    );
  }
  const initial = (me.data?.name ?? me.data?.email ?? "").trim().charAt(0).toUpperCase();
  return (
    <Link className="btn btn-ghost btn-icon" to="/account" aria-label={t("nav.account")} title={t("nav.account")}>
      <span
        style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--c-primary-soft)", color: "var(--c-primary-soft-text)", display: "grid", placeItems: "center", fontWeight: 700 }}
        aria-hidden="true"
      >
        {initial || <Icon name="user" size="sm" />}
      </span>
    </Link>
  );
}

/** AppBar บนมือถือ / TopNav บนเดสก์ท็อป (เมนูกลางตั้งแต่ 1024px) */
export function AppBar() {
  const { t } = useI18n();
  return (
    <header className="appbar">
      <div className="container appbar-inner">
        <Link className="brand" to="/" aria-label={t("web.logo.alt")}>
          <span className="brand-mark">
            <Icon name="brand" />
          </span>
          <span>{t("app.name")}</span>
        </Link>
        <nav className="topnav" aria-label={t("nav.main")}>
          <NavLink to="/" end>
            {t("nav.home")}
          </NavLink>
          <NavLink to="/jobs">{t("nav.jobs")}</NavLink>
          <NavLink to="/pricing">{t("nav.pricing")}</NavLink>
        </nav>
        <div className="appbar-actions">
          <Button variant="primary" className="btn-new" to="/capture" state={{ fresh: true }} iconLeft="camera">
            {t("nav.newEstimate")}
          </Button>
          <LangButton />
          <ThemeButton />
          <AccountSlot />
        </div>
      </div>
    </header>
  );
}

/** แถบล่างบนมือถือ (ซ่อนตั้งแต่ 1024px ด้วย CSS) */
export function BottomTabBar() {
  const { t } = useI18n();
  return (
    <nav className="tabbar" aria-label={t("nav.main")}>
      <NavLink to="/" end>
        <Icon name="home" size="lg" />
        <span>{t("nav.home")}</span>
      </NavLink>
      <NavLink to="/capture" state={{ fresh: true }} className="tab-cam">
        <span className="cam-btn">
          <Icon name="camera" />
        </span>
        <span>{t("nav.capture")}</span>
      </NavLink>
      <NavLink to="/jobs">
        <Icon name="jobs" size="lg" />
        <span>{t("nav.jobs")}</span>
      </NavLink>
    </nav>
  );
}

export interface FlowHeaderProps {
  title: string;
  /** 1-3 (ไม่ส่งเมื่อใช้นอก flow ประเมิน เช่น ดูผลของงานที่บันทึกแล้ว) */
  step?: 1 | 2 | 3;
  backTo?: string;
}

/** FlowHeader (S03 ถึง S06): ย้อนกลับ, ชื่อขั้น + ขั้นที่ n/3, สลับภาษา, ปิด, แถบความคืบหน้า 3 ช่อง */
export function FlowHeader({ title, step, backTo = "/" }: FlowHeaderProps) {
  const { t } = useI18n();
  const nav = useNavigate();
  return (
    <header className="flowhead">
      <div className="container">
        <div className="flowhead-inner">
          <Button variant="ghost" iconOnly iconLeft="left" aria-label={t("nav.back")} title={t("nav.back")} onClick={() => nav(backTo)} />
          <div className="flowhead-title">
            <strong>{title}</strong>
            {step && <span>{t("flow.step", { n: step })}</span>}
          </div>
          <LangButton />
          <Button variant="ghost" iconOnly iconLeft="x" aria-label={t("flow.exit")} title={t("flow.exit")} to="/jobs" />
        </div>
        {step && (
          <div className="flowprog" aria-hidden="true">
            {[1, 2, 3].map((n) => (
              <i key={n} className={n <= step ? "on" : undefined} />
            ))}
          </div>
        )}
      </div>
    </header>
  );
}
